<template>
	<div class="reviews__items">
		<div v-if="isLoading && false" class="reviews__item">
			<div class="review">
				<div class="review__main">
					<p class="review__text">{{ t('loading') }}</p>
				</div>
			</div>
		</div>

		<div v-else-if="reviews.length === 0" class="">
			<div class="review review--empty">
				<div class="review__empty">
					<p class="review__empty-text">{{ t('noReviews') }}</p>
				</div>
			</div>
		</div>

		<div v-for="review in visibleReviews" :key="review.id" class="reviews__item">
			<article class="review">
				<div class="review__heading">
					<div class="review__order">
						<h3 class="review__order-customer">{{ review.customer?.public_name || t('user') }}</h3>
						<div class="review__order-status">
							<span class="review__order-icon" aria-hidden="true">
								<IconCart />
							</span>
							<span>{{ t('orderReceived') }}</span>
						</div>
					</div>

					<div class="review__rating-wrapper">
						<div class="review__rating">
							<div class="visually-hidden">{{ getRatingAria(review.rating)}}</div>
							<span aria-hidden="true" v-html="renderStars(review.rating)"></span>
						</div>
						<div class="review__rating-date">{{ renderReviewDate(review.created_at) }}</div>
					</div>
				</div>
				<div class="review__main">
					<p class="review__text">{{ review.comment }}</p>
					<div class="review__media" v-if="review.images.length">
						<img :src="review.images[0].path" alt="Фото від автора відгуку">
					</div>
					<aside v-if="getAnswerText(review)" class="review__response">
						<div class="review__response-title">
							<h4 class="review__response-name">
								<IconLogo/>
								<!-- <img
									src="/assets/logo.svg"
									role="presentation"
									loading="lazy"
									decoding="async"
								> -->
								{{ t('brandRepresentative') }}
							</h4>
							<div v-if="getAnswerDate(review)" class="review__response-date">{{ getAnswerDate(review) }}</div>
						</div>
						<p class="review__response-text">{{ getAnswerText(review) }}</p>
					</aside>
				</div>
			</article>
		</div>
	</div>
	<div class="review__btn-wrapper">
		<a :href="getReviewsPageUlr()" class="link review__btn">
			<span class="link__text">{{ t('viewAllReviews') }}</span>
			<span class="link__icon">
				<IconArrow />
			</span>
		</a>
	</div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { createReviewsApi } from '@/js/api/reviews.api';
import { createTranslator, setI18nTranslations } from '@/js/i18n';
import { reviewsMainTranslations } from '@/js/i18n/review-translation';

import IconLogo from '@images/logo.svg';
import IconCart from '@images/i-cart-review.svg';
import IconArrow from '@images/i-arrow-slider.svg';

setI18nTranslations(reviewsMainTranslations);

const props = defineProps({
	reviewsUrl: {
		type: String,
		default: '',
	},
});

const reviews = ref(JSON.parse('[]'));
const isLoading = ref(false);
const t = createTranslator();

const getRatingAria = (rating = 0) => String(t('ratingAria')).replace('{rating}', String(rating || 0));

const visibleReviews = computed(() => reviews.value.slice(0, 2));

const renderStars = (rating = 0) => {
	const htmlEmptyStarIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 13.3995L11.8799 15.8549C12.5904 16.3049 13.4599 15.6397 13.2729 14.7984L12.2445 10.1811L15.6756 7.07027C16.302 6.50289 15.9654 5.42682 15.1427 5.35834L10.6271 4.95726L8.86012 0.594284C8.54225 -0.198095 7.45775 -0.198095 7.13988 0.594284L5.3729 4.94748L0.857274 5.34856C0.034552 5.41703 -0.302016 6.4931 0.324375 7.06049L3.7555 10.1713L2.7271 14.7886C2.54012 15.6299 3.40958 16.2951 4.12012 15.8451L8 13.3995Z" fill="#CBCCCC"/></svg>`;
	const htmlFullStarIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 13.3995L11.8799 15.8549C12.5904 16.3049 13.4599 15.6397 13.2729 14.7984L12.2445 10.1811L15.6756 7.07027C16.302 6.50289 15.9654 5.42682 15.1427 5.35834L10.6271 4.95726L8.86012 0.594284C8.54225 -0.198095 7.45775 -0.198095 7.13988 0.594284L5.3729 4.94748L0.857274 5.34856C0.034552 5.41703 -0.302016 6.4931 0.324375 7.06049L3.7555 10.1713L2.7271 14.7886C2.54012 15.6299 3.40958 16.2951 4.12012 15.8451L8 13.3995Z" fill="#FF5100"/></svg>`;

	const safeRating = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
	const full = new Array(safeRating).fill(htmlFullStarIcon).join('');
	const empty = new Array(5 - safeRating).fill(htmlEmptyStarIcon).join('');

	return full + empty;
};

const renderReviewDate = (dateStr = '') => {
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) return '';

	const day = String(date.getDate()).padStart(2, '0');
	const month = t(`months.${date.getMonth()}`);
	const year = date.getFullYear();

	return `${day} ${month} ${year}`;
};

const getAnswerText = (review = {}) => {
	const answer = Reflect.get(review, 'answer');

	if (!answer) return '';
	if (typeof answer === 'string') return answer.trim();
	if (typeof answer === 'object') return String(answer.comment || answer.text || '').trim();

	return '';
};

const getReviewsPageUlr = () => {
	if (props.reviewsUrl) {
		return props.reviewsUrl;
	}

	const handle = window.location.pathname.split('/products/').at(-1);
	const shopify = Reflect.get(window, 'Shopify');
	const locale = shopify && typeof shopify === 'object' ? Reflect.get(shopify, 'locale') : undefined;
	return `${locale === 'ru' ? '/ru' : ''}/products/${handle}/review-${handle}`;
}

const getAnswerDate = (review = {}) => {
	const answer = Reflect.get(review, 'answer');
	if (!answer || typeof answer !== 'object') return '';

	return renderReviewDate(answer.created_at || answer.date || '');
};

const fetchProductReviews = async () => {
	const confSource = Reflect.get(window, 'globalReviewConfProducts');
	const confFactory = Reflect.get(window, 'globalConf');
	const conf = typeof confFactory === 'function' ? confFactory(confSource) : confSource;

	const rawProductId = Reflect.get(window, 'productID');
	const productId = Number(rawProductId);

	if (!conf?.domain || !conf?.methods?.productReviews) return;
	if (!Number.isFinite(productId) || productId <= 0) return;

	const api = createReviewsApi(conf, { refreshTokenEachRequest: true });
	const token = await api.getToken();
	const formattedMethod = conf.methods.productReviews.replace('@id', String(productId));
	const url = new URL(`${conf.domain}${formattedMethod}`);
	url.searchParams.set('shop', conf.shop_name);

	const response = await fetch(url.toString(), {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			Authorization: token,
			...(conf.headers || {}),
		},
		credentials: 'same-origin',
	});

	const data = await response.json();
	if (!response.ok) throw data;

	reviews.value = Array.isArray(data?.data) ? data.data : [];
};

onMounted(async () => {
	isLoading.value = true;
	try {
		await fetchProductReviews();
	} catch (error) {
		console.error(error);
		reviews.value = [];
	} finally {
		isLoading.value = false;
	}
});
</script>
